import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type {Audience} from "crqlar-react-common/dist/models/audience/audience.model";
import type {ReactNode} from "react";
import {useTranslation} from "react-i18next";
import type {MarketingInterestAudienceV4} from "../../models/marketing-v4";

interface LoadingInterestAudienceCardProps {
    loading: true;
}

interface LoadedInterestAudienceCardProps {
    card: MarketingInterestAudienceV4;
    loading: false;
    readOnly: boolean;
    onCreateAudience: (audience: Audience) => void;
}

type InterestAudienceCardProps = LoadingInterestAudienceCardProps | LoadedInterestAudienceCardProps;

export function InterestAudienceCard(props: InterestAudienceCardProps) {
    const {t, i18n} = useTranslation();
    const locale = i18n.resolvedLanguage || i18n.language;
    const compactNumber = new Intl.NumberFormat(locale, {
        notation: "compact",
        maximumFractionDigits: 1,
    });
    let content: ReactNode;

    if (props.loading) {
        content = (
            <Stack sx={{height: "100%"}}>
                <Skeleton animation="wave" width="58%" height={24}/>
                <Skeleton animation="wave" variant="rounded" width={96} height={28} sx={{mt: 1.25}}/>
                <Skeleton animation="wave" width="78%" height={20} sx={{mt: 0.5}}/>
                <Skeleton animation="wave" width={112} height={22} sx={{mt: "auto"}}/>
            </Stack>
        );
    } else {
        const {card, readOnly, onCreateAudience} = props;

        content = (
            <>
                <Typography variant="body1" fontWeight={600} lineHeight={1.4}>
                    {card.label}
                </Typography>

                <Typography variant="h5" component="p" fontSize={24} fontWeight={700} lineHeight={1.15} sx={{mt: 1.25}}>
                    {compactNumber.format(card.guests)}
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
                    {compactNumber.format(card.marketableGuests)} {t("Marketable")}
                    {card.marketablePct != null ? ` · ${card.marketablePct.toFixed(0)}%` : ""}
                </Typography>

                <Box sx={{mt: "auto", pt: 2}}>
                    <Button
                        variant="text"
                        size="small"
                        endIcon={<ArrowForwardRoundedIcon sx={{fontSize: 16}}/>}
                        disabled={readOnly}
                        onClick={() => onCreateAudience(card.audience)}
                        sx={{
                            px: 0,
                            minWidth: 0,
                            textTransform: "none",
                            fontSize: 13,
                            "&:hover": {bgcolor: "transparent", textDecoration: "underline"},
                        }}
                    >
                        {t("Create audience")}
                    </Button>
                </Box>
            </>
        );
    }

    return (
        <Paper
            component="article"
            variant="outlined"
            aria-busy={props.loading}
            sx={{
                width: "100%",
                minWidth: 0,
                minHeight: 170,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                px: {xs: 2, sm: 2.5},
                py: 2,
                borderRadius: 3,
                bgcolor: "background.paper",
                boxSizing: "border-box",
            }}
        >
            {content}
        </Paper>
    );
}

export default InterestAudienceCard;
